import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  Input,
  apiFetch,
  useDrugSearch,
  type AlertTone,
  type Drug,
  type PortalEnrollmentDetail,
  type PortalPrescriptionAddRequest,
  type Prescription,
} from "@dearbill/shared";
import { SearchIcon } from "../icons";
import { DrugResultsListbox } from "./DrugResultsListbox";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

interface AddPrescriptionBarProps {
  enrollmentId: string;
  /** The current list; a search hit already on it reads "Added" and can't be picked. */
  prescriptions: Prescription[];
  /** Portal session bearer for the add. */
  session: string;
  /** Refetches the dashboard after a successful add. */
  reload: () => void;
  /** Called when the add is rejected with 401. */
  onSignOut: () => void;
}

interface Notice {
  tone: AlertTone;
  title: string;
}

/**
 * The search field and "Add Prescription" button over the latest enrollment's
 * list: pick, then confirm. Typing three or more characters opens a listbox of
 * catalog matches; picking one fills the field and arms the button, which
 * POSTs the rxcui and reports the outcome in an Alert under the bar. A success
 * and a 409 (the drug reached the list from elsewhere since the pick) both
 * clear the field and refetch the dashboard, so the list the Alert names is
 * on screen. Typing again drops the pick. The confirm step exists because a
 * prescription cannot be removed.
 *
 * Combobox: the input is `role="combobox"` with `aria-expanded`,
 * `aria-controls` and `aria-activedescendant`; ArrowUp/Down move, Enter picks,
 * Escape and a click outside close, and focusing reopens an unpicked search.
 */
export function AddPrescriptionBar({
  enrollmentId,
  prescriptions,
  session,
  reload,
  onSignOut,
}: AddPrescriptionBarProps): ReactNode {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Drug | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  // A pick fills the field with the drug's name, which is not a new search.
  const search = useDrugSearch(picked === null ? query : "", null, API_BASE);
  const listed = new Set(prescriptions.map((p) => p.rxcui));
  const results = search.results.map((r) => r.drug);
  const showList = open && search.active;
  const active = showList && activeIndex >= 0 ? results[activeIndex] : undefined;

  useEffect(() => {
    if (!showList) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showList]);

  function pick(drug: Drug): void {
    if (listed.has(drug.rxcui)) return;
    setPicked(drug);
    setQuery(drug.label.trim() === "" ? drug.drugName : `${drug.drugName} (${drug.label})`);
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    } else if (!showList || results.length === 0) {
      return;
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && active !== undefined) {
      e.preventDefault();
      pick(active);
    }
  }

  async function submit(drug: Drug): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await apiFetch<PortalEnrollmentDetail>(
        `/api/portal/enrollments/${enrollmentId}/prescriptions`,
        { method: "POST", body: { rxcui: drug.rxcui } satisfies PortalPrescriptionAddRequest },
        { baseUrl: API_BASE, token: session },
      );
      setNotice({ tone: "success", title: `Added ${drug.drugName} to your medications.` });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      if (!(err instanceof ApiError && err.status === 409)) {
        setNotice({ tone: "error", title: "We couldn't add this medication. Please try again." });
        return;
      }
      setNotice({ tone: "warning", title: `${drug.drugName} is already on your list.` });
    } finally {
      setBusy(false);
    }
    setPicked(null);
    setQuery("");
    reload();
  }

  return (
    <>
      <div className="border-haze flex flex-col gap-4 rounded-2xl border bg-white p-5 md:flex-row md:items-center md:gap-5">
        <div ref={rootRef} className="relative min-w-0 flex-1">
          <SearchIcon
            size={20}
            className="text-slate-teal pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2"
          />
          <Input
            label="Search for a prescription to add"
            labelHidden
            placeholder="Search for a prescription to add (e.g. Atorvastatin)..."
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active === undefined ? undefined : `${listId}-${active.rxcui}`}
            autoComplete="off"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPicked(null);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            className="pl-12"
          />
          {showList && (
            <DrugResultsListbox
              id={listId}
              search={search}
              listed={listed}
              activeIndex={activeIndex}
              onPick={pick}
              onHover={setActiveIndex}
            />
          )}
        </div>
        <Button
          variant="primary"
          disabled={picked === null || busy}
          onClick={() => {
            if (picked !== null) void submit(picked);
          }}
          className="shrink-0"
        >
          Add Prescription
        </Button>
      </div>
      {notice !== null && (
        <Alert tone={notice.tone} title={notice.title} onDismiss={() => setNotice(null)} />
      )}
    </>
  );
}
